import type { ReactNode } from 'react';
import { useLingui } from '@lingui/react';
import { UserRound } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { ChildCard } from '@/components/billing/contracts/ChildCard';
import { ContactLink, EntityLink, EntityLinkPlain } from '@/components/shared/entity-links';
import { useContact } from '@/hooks/useContacts';
import { useEmployeePicker } from '@/hooks/useEmployees';
import { useAvatarSrc } from '@/hooks/useImage';
import {
  MAX_CHANGE_REQUEST_CHILD_CARDS,
  changeRequestChildIds,
  parseChangeRequestPayload,
} from '@/types/changeRequest';

interface ChangeRequestSubjectCardsProps {
  payload: unknown;
  /** Clerk user id of the workflow item's creator (a staff requester). */
  createdBy?: string;
  /** Clerk user id -> name; names the impersonating admin and a staff requester without an employee record. */
  userNameMap: Map<string, string>;
}

/**
 * Heads a change request with clickable cards for the child(ren) it is about and
 * for the requester, so the reviewer can open their records before approving.
 * The requester is the parent contact of a parent-app request, else the staff
 * member who raised it.
 */
export function ChangeRequestSubjectCards({ payload, createdBy, userNameMap }: ChangeRequestSubjectCardsProps) {
  const { i18n } = useLingui();
  const parsed = parseChangeRequestPayload(payload);
  if (!parsed) return null;

  const childIds = changeRequestChildIds(parsed);
  const showChildren = childIds.length > 0 && childIds.length <= MAX_CHANGE_REQUEST_CHILD_CARDS;
  const contactId = parsed.extracted?.requested_by_contact_id ?? '';
  const impersonatedBy = parsed.extracted?.impersonated_by_user_id ?? '';

  return (
    <div className="space-y-4" data-testid="change-request-subject-cards">
      {showChildren && (
        <Section
          label={
            childIds.length === 1
              ? i18n._({ id: 'changeRequest.detail.child', message: 'Child' })
              : i18n._({ id: 'changeRequest.subject.children', message: 'Children' })
          }
        >
          {childIds.map((id) => (
            <ChildCard key={id} childId={id} />
          ))}
        </Section>
      )}
      {contactId ? (
        <Section label={i18n._({ id: 'changeRequest.detail.requestedBy', message: 'Requested by' })}>
          <ContactRequesterCard contactId={contactId} impersonatedBy={impersonatedBy} userNameMap={userNameMap} />
        </Section>
      ) : createdBy ? (
        <StaffRequesterSection createdBy={createdBy} userNameMap={userNameMap} />
      ) : null}
    </div>
  );
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
      <div className="mt-1 space-y-2">{children}</div>
    </div>
  );
}

function initialsOf(firstname?: string, lastname?: string): string {
  return `${firstname?.charAt(0)?.toUpperCase() ?? ''}${lastname?.charAt(0)?.toUpperCase() ?? ''}`;
}

/** The ChildCard layout for a person: avatar (image or initials), a link line and a detail line. */
function PersonCard({
  avatarSrc,
  initials,
  alt,
  title,
  subtitle,
}: {
  avatarSrc?: string;
  initials: string;
  alt: string;
  title: ReactNode;
  subtitle?: ReactNode;
}) {
  return (
    <div className="rounded-lg border p-3 bg-card">
      <div className="flex items-center gap-3">
        <Avatar className="h-10 w-10 flex-shrink-0">
          {avatarSrc && <AvatarImage src={avatarSrc} alt={alt} />}
          <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">{initials}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="text-sm leading-tight">{title}</div>
          {subtitle && <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>}
        </div>
      </div>
    </div>
  );
}

function PersonCardSkeleton() {
  return (
    <div className="rounded-lg border p-3 bg-card">
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-32" />
        </div>
      </div>
    </div>
  );
}

function ContactRequesterCard({
  contactId,
  impersonatedBy,
  userNameMap,
}: {
  contactId: string;
  impersonatedBy: string;
  userNameMap: Map<string, string>;
}) {
  const { i18n } = useLingui();
  const { data: contact, isLoading } = useContact(contactId);
  const avatarSrc = useAvatarSrc(contact?.avatarUrl);

  if (isLoading) return <PersonCardSkeleton />;

  const fullName = [contact?.firstname, contact?.lastname].filter(Boolean).join(' ').trim();
  const name = fullName || i18n._({ id: 'changeRequest.detail.unknownContact', message: 'Unknown contact' });
  const label = fullName && contact?.contactInternalId ? `${fullName} (${contact.contactInternalId})` : name;
  // The impersonating admin is named from the Clerk-id keyed map, never by raw id.
  const viaAdmin = !impersonatedBy
    ? ''
    : userNameMap.has(impersonatedBy)
      ? i18n._({ id: 'changeRequest.detail.viaAdmin', message: '(via admin {name})', values: { name: userNameMap.get(impersonatedBy) } })
      : i18n._({ id: 'changeRequest.detail.viaUnknownAdmin', message: '(via an admin)' });
  const role = i18n._({ id: 'changeRequest.requester.parent', message: 'Parent' });

  return (
    <PersonCard
      avatarSrc={avatarSrc}
      initials={initialsOf(contact?.firstname, contact?.lastname)}
      alt={name}
      title={
        <ContactLink contactId={contactId} name={label} showId={false} />
      }
      subtitle={viaAdmin ? `${role} ${viaAdmin}` : role}
    />
  );
}

/**
 * The staff member who raised the request, found by Clerk user id in the employee
 * picker and linked to their employee record. A creator the picker does not know
 * but the name map does (an admin without an employee record) renders unlinked.
 * An unknown creator (an automation principal) renders nothing.
 */
function StaffRequesterSection({ createdBy, userNameMap }: { createdBy: string; userNameMap: Map<string, string> }) {
  const { i18n } = useLingui();
  const { data: employees, isLoading } = useEmployeePicker();
  const employee = (employees ?? []).find((e) => e.clerkUserId === createdBy);
  const label = i18n._({ id: 'changeRequest.detail.requestedBy', message: 'Requested by' });

  if (isLoading) {
    return (
      <Section label={label}>
        <PersonCardSkeleton />
      </Section>
    );
  }

  if (employee) {
    const name = [employee.firstname, employee.lastname].filter(Boolean).join(' ').trim();
    return (
      <Section label={label}>
        <PersonCard
          initials={initialsOf(employee.firstname, employee.lastname)}
          alt={name}
          title={
            <EntityLink to={`/employees/${employee.id}/edit`} icon={UserRound} title={name}>
              {name}
            </EntityLink>
          }
          subtitle={i18n._({ id: 'changeRequest.requester.employee', message: 'Employee' })}
        />
      </Section>
    );
  }

  if (!userNameMap.has(createdBy)) return null;
  const name = userNameMap.get(createdBy) ?? '';
  return (
    <Section label={label}>
      <PersonCard
        initials={name.split(/\s+/).map((part) => part.charAt(0).toUpperCase()).slice(0, 2).join('')}
        alt={name}
        title={
          <EntityLinkPlain icon={UserRound} title={name}>
            {name}
          </EntityLinkPlain>
        }
      />
    </Section>
  );
}
